import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import { api, type User } from "../lib/api";
import { seoulFilenameDate } from "../lib/time";
import { layoutItems } from "../lib/videoLayout";
import { useAreaZoom } from "../hooks/useAreaZoom";
import type { CameraSummary } from "./CamerasPage";
import { MediaDebugOverlay, type MediaDebugStats } from "../components/MediaDebugOverlay";
import { MsePlaybackUnsupportedError, RecordedMseSession, mseStartupSeekTarget, type MsePlaybackChunk } from "../lib/msePlayback";

export interface TimelineRange { startAt: string; endAt: string; segmentCount: number }
interface MotionRange { startAt: string; endAt: string }
interface TimelineSegment { id: string; startAt: string; endAt: string; durationMs: number; fileSize: number; codec: string | null; resolution: string | null; fps: number | null; locked: boolean }
interface TimelineResponse {
  cameraId: string;
  date: string;
  timezone: string;
  dayStart: string;
  dayEnd: string;
  ranges: TimelineRange[];
  motionRanges: MotionRange[];
  motionAnalysis: { analyzedSegments: number; pendingSegments: number; failedSegments: number; totalSegments: number };
  segments: TimelineSegment[];
}
interface ExportJob { id: string; cameraName: string; startAt: string; endAt: string; status: string; progress: number; fileSize: number | null; error: string | null; createdAt: string }
type SeekDirection = "forward" | "backward";
type PlaybackRecoveryReason = "stale" | "error" | "ended";
interface PlaybackRecoveryAuditWindow { lastReportedAt: number; suppressedCount: number }
interface ActivePlaybackRecovery {
  reason: PlaybackRecoveryReason;
  reported: boolean;
  suppressedCount: number;
  startedAt: number;
}
interface TimelineHover { position: number; time: string }
interface TimelineWindow { start: number; end: number }
interface TimelineDrag { pointerId: number; startX: number; width: number; view: TimelineWindow; moved: boolean }
interface TilePlaybackSource { start: Date; end: Date; url: string; revision: number }
interface TilePlayback {
  cameraId: string;
  start: Date | null;
  end: Date | null;
  url: string;
  revision: number;
  preload: TilePlaybackSource | null;
}
interface SharedPlaybackClock {
  timeMs: number | null;
  anchoredAt: number;
  playing: boolean;
  rate: number;
}
interface PlaybackMediaDiagnostic {
  event: "media_error" | "play_rejected" | "stale_timeout" | "load_timeout" | "ended";
  errorCode: number | null;
  errorMessage: string | null;
  networkState: number;
  readyState: number;
  currentTime: number | null;
  duration: number | null;
  videoWidth: number;
  videoHeight: number;
  playbackRate: number | null;
  clockDriftSeconds: number | null;
  bufferedStart: number | null;
  bufferedEnd: number | null;
  droppedFrames: number | null;
}

interface LocalPlaybackRecovery {
  mediaKey: string;
  startedAt: number;
  targetTime: number;
}

class MseChunkDurationMismatchError extends Error {
  constructor(readonly chunkUrl: string, appendedSeconds: number, expectedSeconds: number) {
    super(`MSE chunk duration mismatch (${appendedSeconds.toFixed(2)}s / ${expectedSeconds.toFixed(2)}s)`);
    this.name = "MseChunkDurationMismatchError";
  }
}

const playbackRates = [0.25, 0.5, 1, 2, 4, 8, 16];
const seekSteps = [
  { seconds: 1, label: "1초" },
  { seconds: 2, label: "2초" },
  { seconds: 3, label: "3초" },
  { seconds: 5, label: "5초" },
  { seconds: 10, label: "10초" },
  { seconds: 30, label: "30초" },
  { seconds: 60, label: "1분" },
  { seconds: 180, label: "3분" },
  { seconds: 300, label: "5분" }
];
const timelineZoomDurations = [86_400_000, 43_200_000, 21_600_000, 10_800_000, 3_600_000, 1_800_000];
const defaultTimelineZoomLevel = 2;
const timelineFocusRatio = 2 / 3;
const minimumTimelineLoadPaddingMs = 21_600_000;
const liveTimelineRefreshGraceMs = 300_000;
const playbackStaleTimeoutMs = 20_000;
const localPlaybackRecoveryValidationMs = 5_000;
const localPlaybackRecoveryMinimumAdvanceSeconds = 0.25;
const directPlaybackCatchUpIntervalMs = 10_000;
const directPlaybackCatchUpDriftSeconds = 3;
const playbackLoadUnlockMs = 30_000;
const staleRecoveryAuditIntervalMs = 10 * 60_000;
const errorRecoveryAuditIntervalMs = 60_000;
// Physical recording files start on keyframes and do not repeat encoded frames
// across files. Preload early enough that the next file is buffered before the
// shared clock reaches its boundary, including at high playback rates.
const playbackPreloadMinimumMs = 30_000;
const playbackPreloadWallSeconds = 10;
const playbackPreloadRetryMs = 10_000;
const playbackEndToleranceSeconds = 2;

export function PlaybackPage({ user }: { user: User }) {
  const playerRef = useRef<HTMLDivElement>(null);
  const timelineRequestRef = useRef(0);
  const timelineDataRef = useRef<TimelineResponse | null>(null);
  const timelinesByCameraRef = useRef<Record<string, TimelineResponse>>({});
  const playbackTimelinesByCameraRef = useRef<Record<string, TimelineResponse>>({});
  const timelineElementRef = useRef<HTMLDivElement>(null);
  const timelineWheelHandlerRef = useRef<(event: globalThis.WheelEvent) => void>(() => undefined);
  const timelineLoadInFlightRef = useRef(0);
  const initialTimelineWindowRef = useRef(defaultTimelineWindow(new Date()));
  const timelineWindowRef = useRef<TimelineWindow>(initialTimelineWindowRef.current);
  const timelineZoomLevelRef = useRef(defaultTimelineZoomLevel);
  const timelineDragRef = useRef<TimelineDrag | null>(null);
  const suppressTimelineClickRef = useRef(false);
  const timelineZoomFocusRef = useRef<{ time: number; position: number } | null>(null);
  const sharedClockRef = useRef<SharedPlaybackClock>({ timeMs: null, anchoredAt: performance.now(), playing: false, rate: 1 });
  const playIntentRef = useRef(false);
  const suspendPlaybackRef = useRef<(message: string) => void>(() => undefined);
  const tileVideoRefs = useRef(new Map<string, HTMLVideoElement>());
  const tileMediaOriginsRef = useRef(new Map<string, number>());
  const tilePlaybacksRef = useRef<Record<string, TilePlayback>>({});
  const tileRecoveryAtRef = useRef(new Map<string, number>());
  const tileRecoveryTimersRef = useRef(new Map<string, number>());
  const tileRecoveringRef = useRef(new Map<string, ActivePlaybackRecovery>());
  const playbackRecoveryAuditRef = useRef(new Map<string, PlaybackRecoveryAuditWindow>());
  const tilePreloadRetryAtRef = useRef(new Map<string, number>());
  const playbackRevisionRef = useRef(0);
  const playbackLoadPendingRef = useRef(new Set<string>());
  const playbackLoadTimerRef = useRef<number | null>(null);
  const [cameras, setCameras] = useState<CameraSummary[]>([]);
  const [playbackSlots, setPlaybackSlots] = useState<Array<CameraSummary | null>>([]);
  const [cameraId, setCameraId] = useState("");
  const [selectedCameraIds, setSelectedCameraIds] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [timelinesByCamera, setTimelinesByCamera] = useState<Record<string, TimelineResponse>>({});
  const [tilePlaybacks, setTilePlaybacks] = useState<Record<string, TilePlayback>>({});
  const [absoluteTime, setAbsoluteTime] = useState<Date | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === "visible");
  const [stopped, setStopped] = useState(false);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [timelineHover, setTimelineHover] = useState<TimelineHover | null>(null);
  const [timelineWindow, setTimelineWindow] = useState<TimelineWindow>(initialTimelineWindowRef.current);
  const [timelineZoomLevel, setTimelineZoomLevel] = useState(defaultTimelineZoomLevel);
  const [timelineDragging, setTimelineDragging] = useState(false);
  const [rate, setRate] = useState(1);
  const [audioCameraId, setAudioCameraId] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [message, setMessage] = useState("");
  const [jumpAt, setJumpAt] = useState(`${seoulToday()}T00:00`);
  const [exports, setExports] = useState<ExportJob[]>([]);
  const [exportCameraId, setExportCameraId] = useState("");
  const [exportStart, setExportStart] = useState(`${seoulToday()}T00:00`);
  const [exportEnd, setExportEnd] = useState(`${seoulToday()}T00:05`);
  const selectedCameraKey = selectedCameraIds.join(",");

  tilePlaybacksRef.current = tilePlaybacks;

  const canSearch = user.permissions.includes("recording.search");
  const canPlay = user.permissions.includes("recording.play");
  const canDownload = user.permissions.includes("recording.download");
  const canDelete = user.permissions.includes("recording.delete");
  const canSnapshot = user.permissions.includes("snapshot.create");
  const canAudio = user.permissions.includes("audio.output");
  const canConfigure = user.permissions.includes("recording.configure");
  const canDebug = user.permissions.includes("debug.view");

  const finishPlaybackLoad = () => {
    playbackLoadPendingRef.current.clear();
    if (playbackLoadTimerRef.current !== null) window.clearTimeout(playbackLoadTimerRef.current);
    playbackLoadTimerRef.current = null;
    setPlaybackLoading(false);
  };

  const beginPlaybackLoad = (target: Date) => {
    const pending = selectedCameraIds.filter((selectedId) => {
      const source = playbackTimelinesByCameraRef.current[selectedId];
      return Boolean(source && recordingRangeAt(source.ranges, target));
    });
    finishPlaybackLoad();
    if (!pending.length) return;
    playbackLoadPendingRef.current = new Set(pending);
    setPlaybackLoading(true);
    playbackLoadTimerRef.current = window.setTimeout(finishPlaybackLoad, playbackLoadUnlockMs);
  };

  const markPlaybackLoaded = (id: string, revision: number) => {
    // One playable tile is enough to make the shared controls usable. Other
    // cameras continue buffering independently and join when ready.
    if (!isCurrentPlaybackRevision(tilePlaybacksRef.current[id]?.revision, revision)) return;
    if (!playbackLoadPendingRef.current.has(id)) return;
    finishPlaybackLoad();
  };

  const nextPlaybackRevision = () => {
    playbackRevisionRef.current += 1;
    return playbackRevisionRef.current;
  };

  const resetTileRecovery = () => {
    for (const timer of tileRecoveryTimersRef.current.values()) window.clearTimeout(timer);
    tileRecoveryTimersRef.current.clear();
    tileRecoveryAtRef.current.clear();
    tileRecoveringRef.current.clear();
    tilePreloadRetryAtRef.current.clear();
  };

  const releaseTileVideos = () => {
    const videos = new Set<HTMLVideoElement>(tileVideoRefs.current.values());
    playerRef.current?.querySelectorAll<HTMLVideoElement>("video.playback-video").forEach((video) => videos.add(video));
    for (const video of videos) releaseVideoElement(video);
    tileVideoRefs.current.clear();
    tileMediaOriginsRef.current.clear();
  };

  const updateTimelineWindow = (next: TimelineWindow) => {
    timelineWindowRef.current = next;
    setTimelineWindow(next);
  };

  const storeTimeline = (next: TimelineResponse | null) => {
    timelineDataRef.current = next;
    setTimeline(next);
    if (cameraId && next) {
      const updated = { ...timelinesByCameraRef.current, [cameraId]: next };
      timelinesByCameraRef.current = updated;
      setTimelinesByCamera(updated);
    }
  };

  const loadTimelineWindow = async (view: TimelineWindow, announce = false, initializeInputs = false, preserveOnError = false): Promise<TimelineResponse | null> => {
    if (!cameraId || !canSearch) return null;
    if (preserveOnError && timelineLoadInFlightRef.current > 0) return timelineDataRef.current;
    timelineLoadInFlightRef.current += 1;
    const requestId = ++timelineRequestRef.current;
    if (announce) setMessage("타임라인을 불러오는 중입니다…");
    const padding = timelineLoadPadding(view);
    const start = new Date(view.start - padding);
    const end = new Date(view.end + padding);
    try {
      const requestedCameraIds = selectedCameraIds.includes(cameraId) ? selectedCameraIds : [cameraId, ...selectedCameraIds];
      const entries = await Promise.all(requestedCameraIds.map(async (selectedId) => {
        const params = new URLSearchParams({ cameraId: selectedId, start: start.toISOString(), end: end.toISOString() });
        return [selectedId, await api<TimelineResponse>(`/api/recordings/timeline?${params}`)] as const;
      }));
      if (requestId !== timelineRequestRef.current) return null;
      const results = Object.fromEntries(entries) as Record<string, TimelineResponse>;
      const result = results[cameraId] ?? null;
      timelinesByCameraRef.current = results;
      const playbackTimeMs = sharedClockRef.current.timeMs;
      if (playbackTimeMs === null || Object.values(results).some((item) => timelineResponseCovers(item, playbackTimeMs))) {
        playbackTimelinesByCameraRef.current = results;
      }
      setTimelinesByCamera(results);
      timelineDataRef.current = result;
      setTimeline(result);
      if (announce) {
        const hasVisibleRecording = Object.values(results).some((item) => item.ranges.some((range) => new Date(range.endAt).getTime() > view.start && new Date(range.startAt).getTime() < view.end));
        setMessage(hasVisibleRecording ? "" : "현재 보이는 시간대에 녹화 영상이 없습니다.");
      }
      if (initializeInputs && result) {
        const reference = new Date(timelineReferenceTime(view));
        const resolved = resolveRecordingTarget(result.ranges, reference, "forward");
        const inputTime = resolved?.at ?? reference;
        setJumpAt(toSeoulInput(inputTime));
        setExportStart(toSeoulInput(inputTime));
        setExportEnd(toSeoulInput(new Date(inputTime.getTime() + 300_000)));
      }
      return result;
    } catch (error) {
      if (requestId !== timelineRequestRef.current) return null;
      if (preserveOnError) return timelineDataRef.current;
      timelinesByCameraRef.current = {};
      setTimelinesByCamera({});
      storeTimeline(null);
      setMessage(error instanceof Error ? error.message : "타임라인 조회 실패");
      return null;
    } finally {
      timelineLoadInFlightRef.current = Math.max(0, timelineLoadInFlightRef.current - 1);
    }
  };

  const ensureTimelineWindow = (view: TimelineWindow) => {
    const loaded = selectedCameraIds.map((id) => timelinesByCameraRef.current[id]);
    if (!loaded.length || loaded.some((item) => !item || view.start < new Date(item.dayStart).getTime() || view.end > new Date(item.dayEnd).getTime())) {
      void loadTimelineWindow(view);
    }
  };

  useEffect(() => {
    if (!canSearch) return;
    void api<CameraSummary[]>("/api/camera-options?scope=playback").then((items) => {
      const arranged = layoutItems(items.filter((item) => item.enabled), user.cameraLayout, user.videoLayout);
      const selectedIds = arranged.filter((item): item is CameraSummary => Boolean(item)).map((item) => item.id);
      setCameras(items);
      setPlaybackSlots(arranged);
      setCameraId(selectedIds[0] ?? "");
      setSelectedCameraIds(selectedIds);
      setAudioCameraId((current) => current && selectedIds.includes(current) ? current : null);
      setExportCameraId((current) => items.some((camera) => camera.id === current && camera.enabled)
        ? current
        : selectedIds[0] ?? items.find((camera) => camera.enabled)?.id ?? "");
    }).catch((error) => setMessage(error instanceof Error ? error.message : "카메라 목록 조회 실패"));
  }, [canSearch, user.videoLayout, user.cameraLayout]);

  useEffect(() => {
    if (!cameraId || !canSearch || !selectedCameraIds.length) return;
    setTilePlaybacks({});
    playbackTimelinesByCameraRef.current = {};
    releaseTileVideos();
    resetTileRecovery();
    finishPlaybackLoad();
    playIntentRef.current = false;
    sharedClockRef.current = { timeMs: null, anchoredAt: performance.now(), playing: false, rate: 1 };
    setAbsoluteTime(null);
    setPlaying(false);
    setStopped(false);
    setRate(1);
    void loadTimelineWindow(timelineWindowRef.current, true, true);
  }, [cameraId, canSearch, selectedCameraKey]);

  useEffect(() => () => {
    for (const timer of tileRecoveryTimersRef.current.values()) window.clearTimeout(timer);
    if (playbackLoadTimerRef.current !== null) window.clearTimeout(playbackLoadTimerRef.current);
    releaseTileVideos();
  }, []);

  useEffect(() => {
    if (!cameraId || !canSearch || !selectedCameraIds.length) return;
    const refreshTimeline = () => {
      if (document.visibilityState !== "visible" || timelineLoadInFlightRef.current > 0) return;
      if (!shouldRefreshTimelineWindow(timelineWindowRef.current)) return;
      void loadTimelineWindow(timelineWindowRef.current, false, false, true);
    };
    const timer = window.setInterval(refreshTimeline, 30_000);
    const handleVisibility = () => { if (document.visibilityState === "visible") refreshTimeline(); };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [cameraId, canSearch, selectedCameraKey]);

  useEffect(() => {
    const handlePlaybackVisibility = () => {
      const visible = document.visibilityState === "visible";
      setPageVisible(visible);
      if (visible) return;
      setMuted(true);
      suspendPlaybackRef.current("브라우저가 백그라운드 영상 재생을 제한하여 재생을 일시정지했습니다. 화면으로 돌아온 뒤 재생 버튼을 누르세요.");
    };
    document.addEventListener("visibilitychange", handlePlaybackVisibility);
    return () => document.removeEventListener("visibilitychange", handlePlaybackVisibility);
  }, []);

  useEffect(() => {
    if (canAudio) return;
    setAudioCameraId(null);
    setMuted(true);
  }, [canAudio]);

  useEffect(() => {
    const element = timelineElementRef.current;
    if (!element) return;
    const handleWheel = (event: globalThis.WheelEvent) => timelineWheelHandlerRef.current(event);
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, [timeline, selectedCameraKey]);

  const loadExports = () => canDownload ? api<ExportJob[]>("/api/recordings/exports").then(setExports) : Promise.resolve();
  useEffect(() => {
    if (!canDownload) return;
    void loadExports();
  }, [canDownload]);

  const hasActiveExport = exports.some((job) => job.status === "queued" || job.status === "running");
  useEffect(() => {
    if (!canDownload || !hasActiveExport) return;
    const timer = setInterval(() => void loadExports(), 3000);
    return () => clearInterval(timer);
  }, [canDownload, hasActiveExport]);

  const visibleSegments = useMemo(() => timeline?.segments.filter((segment) => (
    new Date(segment.endAt).getTime() > timelineWindow.start && new Date(segment.startAt).getTime() < timelineWindow.end
  )) ?? [], [timeline, timelineWindow]);
  const selectedCameras = useMemo(() => selectedCameraIds.map((id) => cameras.find((camera) => camera.id === id)).filter((camera): camera is CameraSummary => Boolean(camera)), [cameras, selectedCameraKey]);

  useEffect(() => {
    if (!playing) return;
    const update = () => {
      const current = currentPlaybackTime();
      if (current) setAbsoluteTime(current);
    };
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [playing, rate]);

  useEffect(() => {
    if (!absoluteTime || !canPlay || !selectedCameraIds.length || stopped) return;
    const currentMs = absoluteTime.getTime();
    const hasRecordingNow = selectedCameraIds.some((selectedId) => {
      const source = playbackTimelinesByCameraRef.current[selectedId];
      return Boolean(source && recordingRangeAt(source.ranges, absoluteTime));
    });

    if (!hasRecordingNow && playing) {
      const next = selectedCameraIds.flatMap((selectedId) => {
        const source = playbackTimelinesByCameraRef.current[selectedId];
        const resolved = source ? resolveRecordingTarget(source.ranges, absoluteTime, "forward") : null;
        return resolved ? [resolved.at] : [];
      }).sort((left, right) => left.getTime() - right.getTime())[0];
      if (next) {
        jumpToTarget(next, "forward", rate, true, playbackTimelinesByCameraRef.current);
      } else {
        setSharedClock(absoluteTime, false, rate);
        setMessage(`${formatSeoul(absoluteTime.toISOString())} 이후에는 녹화 영상이 없습니다.`);
      }
      return;
    }

    setTilePlaybacks((current) => {
      let changed = false;
      const next = { ...current };
      for (const selectedId of selectedCameraIds) {
        const existing = current[selectedId];
        const source = playbackTimelinesByCameraRef.current[selectedId];
        const range = source ? recordingRangeAt(source.ranges, absoluteTime) : null;
        const coversCurrentTime = Boolean(existing?.url && existing.start && existing.end
          && currentMs >= existing.start.getTime() && currentMs < existing.end.getTime());
        if (range && !coversCurrentTime) {
          const preloaded = existing?.preload;
          next[selectedId] = preloaded && currentMs >= preloaded.start.getTime() && currentMs < preloaded.end.getTime()
            ? { cameraId: selectedId, ...preloaded, preload: null }
            : tilePlaybackAt(selectedId, absoluteTime, nextPlaybackRevision());
          changed = true;
        } else if (!range && existing?.url) {
          next[selectedId] = emptyTilePlayback(selectedId, nextPlaybackRevision());
          changed = true;
        } else if (range && existing?.url && existing.end) {
          const remainingMs = existing.end.getTime() - currentMs;
          const preloadLeadMs = Math.max(playbackPreloadMinimumMs, rate * playbackPreloadWallSeconds * 1000);
          const rangeEndMs = new Date(range.endAt).getTime();
          const mayRetryPreload = Date.now() >= (tilePreloadRetryAtRef.current.get(selectedId) ?? 0);
          if (remainingMs <= preloadLeadMs && existing.end.getTime() < rangeEndMs && !existing.preload && mayRetryPreload) {
            const preload = playbackSourceAt(selectedId, existing.end, nextPlaybackRevision());
            if (preload) {
              next[selectedId] = { ...existing, preload };
              changed = true;
            }
          }
        }
      }
      return changed ? next : current;
    });
  }, [absoluteTime, canPlay, playing, rate, selectedCameraKey, stopped]);

  if (!canSearch) return <>
    <div className="page-heading"><div><p className="eyebrow">시간별 녹화 내역</p><h2>녹화 영상 재생</h2></div></div>
    <div className="panel empty-state">녹화영상 검색 권한이 없습니다.</div>
  </>;

  const currentPlaybackTime = (): Date | null => {
    const clock = sharedClockRef.current;
    if (clock.timeMs === null) return null;
    if (!clock.playing) return new Date(clock.timeMs);

    // The timeline follows media that is actually decoding, not the wall clock. A slow
    // or disconnected tile cannot hold the other cameras, while a stall affecting every
    // tile no longer advances the cursor into sources which have not produced a frame.
    let mediaTimeMs: number | null = null;
    for (const [id, video] of tileVideoRefs.current) {
      const source = tilePlaybacksRef.current[id];
      if (!source?.start || !source.url || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !Number.isFinite(video.currentTime)) continue;
      const mediaOriginMs = tileMediaOriginsRef.current.get(id) ?? source.start.getTime();
      const candidate = mediaOriginMs + video.currentTime * 1000;
      mediaTimeMs = mediaTimeMs === null ? candidate : Math.max(mediaTimeMs, candidate);
    }
    if (mediaTimeMs !== null) clock.timeMs = Math.max(clock.timeMs, mediaTimeMs);
    return new Date(clock.timeMs);
  };

  const setSharedClock = (time: Date, shouldPlay: boolean, nextRate: number) => {
    playIntentRef.current = shouldPlay;
    sharedClockRef.current = { timeMs: time.getTime(), anchoredAt: performance.now(), playing: shouldPlay, rate: nextRate };
    setAbsoluteTime(time);
    setPlaying(shouldPlay);
    setRate(nextRate);
  };

  const suspendPlayback = (notice: string) => {
    if (!playIntentRef.current) return;
    const current = currentPlaybackTime();
    if (!current) return;
    setSharedClock(current, false, sharedClockRef.current.rate);
    for (const video of tileVideoRefs.current.values()) video.pause();
    setMessage(notice);
  };
  suspendPlaybackRef.current = suspendPlayback;

  const armSharedClock = (time: Date, nextRate: number) => {
    playIntentRef.current = true;
    sharedClockRef.current = { timeMs: time.getTime(), anchoredAt: performance.now(), playing: false, rate: nextRate };
    setAbsoluteTime(time);
    setPlaying(true);
    setRate(nextRate);
  };

  const startSharedClockFromTile = (id: string, revision: number, time: Date) => {
    if (!isCurrentPlaybackRevision(tilePlaybacksRef.current[id]?.revision, revision)) return;
    const clock = sharedClockRef.current;
    if (!playIntentRef.current || clock.playing) return;
    sharedClockRef.current = { timeMs: time.getTime(), anchoredAt: performance.now(), playing: true, rate: clock.rate };
    setAbsoluteTime(time);
  };

  const tilePlaybackAt = (selectedCameraId: string, target: Date, revision: number): TilePlayback => {
    const playback = playbackSourceAt(selectedCameraId, target, revision);
    return playback ? { cameraId: selectedCameraId, ...playback, preload: null } : emptyTilePlayback(selectedCameraId, revision);
  };

  const playbackSourceAt = (selectedCameraId: string, target: Date, revision: number): TilePlaybackSource | null => {
    const source = playbackTimelinesByCameraRef.current[selectedCameraId];
    const segment = source ? playbackSegmentAt(source.segments, target) : null;
    if (!segment) return null;
    return {
      start: new Date(segment.startAt),
      end: new Date(segment.endAt),
      url: playbackUrlWithRepresentation(
        `/api/recordings/segments/${segment.id}/play`,
        requiresNativePlayback(segment.codec) ? "native" : "mse"
      ),
      revision
    };
  };

  const emptyTilePlayback = (selectedCameraId: string, revision: number): TilePlayback => ({
    cameraId: selectedCameraId,
    start: null,
    end: null,
    url: "",
    revision,
    preload: null
  });

  const synchronizeAllTiles = (target: Date) => {
    setTilePlaybacks((current) => Object.fromEntries(selectedCameraIds.map((selectedId) => [
      selectedId,
      tilePlaybackAt(selectedId, target, nextPlaybackRevision())
    ])));
  };

  const beginSharedPlayback = (start: Date, initialRate: number, shouldPlay: boolean, sources: Record<string, TimelineResponse>) => {
    // A user seek supersedes every retry scheduled for the previous position.
    // If these timers survive, they replace the newly loading video and start a
    // self-sustaining request/recovery loop.
    resetTileRecovery();
    releaseTileVideos();
    playbackTimelinesByCameraRef.current = sources;
    beginPlaybackLoad(start);
    synchronizeAllTiles(start);
    setStopped(false);
    if (shouldPlay) armSharedClock(start, initialRate);
    else setSharedClock(start, false, initialRate);
    setMessage("");
    setExportStart(toSeoulInput(start));
    setExportEnd(toSeoulInput(new Date(start.getTime() + 300_000)));
  };

  const jumpToTarget = (
    target: Date,
    direction: SeekDirection = "forward",
    initialRate = 1,
    shouldPlay = true,
    sources: Record<string, TimelineResponse> = timelinesByCameraRef.current
  ) => {
    if (!timeline) return;
    if (!canPlay) {
      setMessage("녹화영상 재생 권한이 없습니다.");
      return;
    }
    const candidates = selectedCameraIds.flatMap((selectedId) => {
      const source = sources[selectedId];
      const resolved = source ? resolveRecordingTarget(source.ranges, target, direction) : null;
      return resolved ? [{ cameraId: selectedId, ...resolved }] : [];
    });
    const exact = candidates.filter((candidate) => !candidate.adjusted);
    const resolved = exact.length
      ? exact.reduce((best, candidate) => new Date(candidate.range.endAt).getTime() > new Date(best.range.endAt).getTime() ? candidate : best)
      : candidates.reduce<typeof candidates[number] | null>((best, candidate) => {
          if (!best) return candidate;
          return direction === "forward"
            ? candidate.at.getTime() < best.at.getTime() ? candidate : best
            : candidate.at.getTime() > best.at.getTime() ? candidate : best;
        }, null);
    if (!resolved) {
      setJumpAt(toSeoulInput(target));
      setMessage(direction === "forward"
        ? `${formatSeoul(target.toISOString())} 이후에는 녹화 영상이 없습니다.`
        : `${formatSeoul(target.toISOString())} 이전에는 녹화 영상이 없습니다.`);
      return;
    }
    setJumpAt(toSeoulInput(resolved.at));
    beginSharedPlayback(resolved.at, initialRate, shouldPlay, sources);
    if (resolved.adjusted) {
      setMessage(`${formatSeoul(target.toISOString())}에는 녹화가 없어 ${formatSeoul(resolved.at.toISOString())}부터 재생합니다.`);
    }
  };

  const timelinePoint = (element: HTMLDivElement, clientX: number, view = timelineWindowRef.current) => {
    const bounds = element.getBoundingClientRect();
    const position = bounds.width ? Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width)) : 0;
    return { position, time: new Date(Math.min(view.end - 1, view.start + (view.end - view.start) * position)) };
  };

  const selectTimeline = (event: MouseEvent<HTMLDivElement>) => {
    if (!timeline || playbackLoading) return;
    if (suppressTimelineClickRef.current) {
      suppressTimelineClickRef.current = false;
      return;
    }
    const point = timelinePoint(event.currentTarget, event.clientX);
    if (point) {
      timelineZoomFocusRef.current = { time: point.time.getTime(), position: point.position };
      jumpToTarget(point.time, "forward", 1);
    }
  };

  const jumpToTime = async () => {
    if (!timeline || !jumpAt || playbackLoading) return;
    const target = new Date(fromSeoulInput(jumpAt));
    const duration = timelineZoomDurations[timelineZoomLevelRef.current] ?? timelineZoomDurations[defaultTimelineZoomLevel]!;
    const nextView = timelineWindowAt(target, duration, timelineFocusRatio);
    timelineZoomFocusRef.current = { time: target.getTime(), position: timelineFocusRatio };
    updateTimelineWindow(nextView);
    const loaded = timelineDataRef.current;
    if (!loaded || nextView.start < new Date(loaded.dayStart).getTime() || nextView.end > new Date(loaded.dayEnd).getTime()) {
      await loadTimelineWindow(nextView, true);
    }
    jumpToTarget(target, "forward", 1);
  };

  const beginTimelineDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    timelineDragRef.current = { pointerId: event.pointerId, startX: event.clientX, width: Math.max(1, bounds.width), view: timelineWindowRef.current, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveTimelineDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = timelineDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < 4) return;
    drag.moved = true;
    setTimelineDragging(true);
    timelineZoomFocusRef.current = null;
    const duration = drag.view.end - drag.view.start;
    const shift = -(delta / drag.width) * duration;
    updateTimelineWindow({ start: drag.view.start + shift, end: drag.view.end + shift });
  };

  const finishTimelineDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = timelineDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    timelineDragRef.current = null;
    setTimelineDragging(false);
    if (!drag.moved) return;
    suppressTimelineClickRef.current = true;
    window.setTimeout(() => { suppressTimelineClickRef.current = false; }, 0);
    const view = timelineWindowRef.current;
    ensureTimelineWindow(view);
  };

  const zoomTimeline = (direction: -1 | 1, focus?: { time: number; position: number }) => {
    const currentLevel = timelineZoomLevelRef.current;
    const nextLevel = Math.min(timelineZoomDurations.length - 1, Math.max(0, currentLevel + direction));
    if (nextLevel === currentLevel) return;
    const view = timelineWindowRef.current;
    const selectedFocus = focus ?? timelineZoomFocusRef.current ?? { time: timelineReferenceTime(view), position: timelineFocusRatio };
    const nextDuration = timelineZoomDurations[nextLevel]!;
    const nextView = { start: selectedFocus.time - nextDuration * selectedFocus.position, end: selectedFocus.time + nextDuration * (1 - selectedFocus.position) };
    timelineZoomFocusRef.current = selectedFocus;
    timelineZoomLevelRef.current = nextLevel;
    setTimelineZoomLevel(nextLevel);
    updateTimelineWindow(nextView);
    ensureTimelineWindow(nextView);
  };

  const wheelTimeline = (event: globalThis.WheelEvent) => {
    if (!timeline || event.deltaY === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const element = timelineElementRef.current;
    if (!element) return;
    const point = timelinePoint(element, event.clientX);
    zoomTimeline(event.deltaY < 0 ? 1 : -1, { time: point.time.getTime(), position: point.position });
  };
  timelineWheelHandlerRef.current = wheelTimeline;

  const seekBy = (seconds: number) => {
    if (playbackLoading) return;
    const current = currentPlaybackTime();
    if (!current) return;
    const direction: SeekDirection = seconds >= 0 ? "forward" : "backward";
    const target = new Date(current.getTime() + seconds * 1000);
    jumpToTarget(target, direction, rate, playing, playbackTimelinesByCameraRef.current);
  };

  const play = () => {
    const current = currentPlaybackTime();
    if (!current) return;
    if (stopped) {
      beginPlaybackLoad(current);
      synchronizeAllTiles(current);
      armSharedClock(current, rate);
    } else {
      setSharedClock(current, true, rate);
    }
    setStopped(false);
  };

  const pause = () => {
    const current = currentPlaybackTime();
    if (!current || !playing) return;
    setSharedClock(current, false, rate);
    for (const video of tileVideoRefs.current.values()) video.pause();
  };

  const stop = () => {
    const current = currentPlaybackTime();
    if (!current || !playing) return;
    for (const video of tileVideoRefs.current.values()) video.pause();
    setSharedClock(current, false, rate);
    setTilePlaybacks({});
    releaseTileVideos();
    resetTileRecovery();
    finishPlaybackLoad();
    setStopped(true);
  };

  const clearPlayback = () => {
    releaseTileVideos();
    setTilePlaybacks({});
    resetTileRecovery();
    finishPlaybackLoad();
    playIntentRef.current = false;
    sharedClockRef.current = { timeMs: null, anchoredAt: performance.now(), playing: false, rate };
    setAbsoluteTime(null);
    setPlaying(false);
    setStopped(false);
  };

  const changeRate = (next: number) => {
    const current = currentPlaybackTime();
    if (current && playing && !sharedClockRef.current.playing) armSharedClock(current, next);
    else if (current) setSharedClock(current, playing, next);
    else setRate(next);
    for (const video of tileVideoRefs.current.values()) video.playbackRate = next;
  };

  const changeVolume = (next: number) => {
    if (!audioCameraId) return;
    setVolume(next);
    setMuted(next === 0);
    for (const video of tileVideoRefs.current.values()) video.volume = next;
  };

  const toggleMute = () => {
    if (!audioCameraId) {
      setMessage("소리를 들을 카메라 화면을 먼저 선택하세요.");
      return;
    }
    const next = !muted;
    setMuted(next);
  };

  const selectAudioCamera = (id: string) => {
    setAudioCameraId(id);
    if (muted) setMessage("오디오 대상을 선택했습니다. 소리는 스피커 버튼을 눌러야 재생됩니다.");
  };

  const snapshot = async () => {
    if (!absoluteTime) return;
    const snapshotCameraId = selectedCameraIds.find((selectedId) => {
      const source = playbackTimelinesByCameraRef.current[selectedId];
      return Boolean(source && recordingRangeAt(source.ranges, absoluteTime));
    });
    if (!snapshotCameraId) {
      setMessage("현재 시각에 저장할 수 있는 녹화 화면이 없습니다.");
      return;
    }
    const response = await fetch(`/api/recordings/${snapshotCameraId}/snapshot?at=${encodeURIComponent(absoluteTime.toISOString())}&format=jpeg`, { credentials: "same-origin" });
    if (!response.ok) {
      setMessage(`현재 화면 저장 실패 (${response.status})`);
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = `playback-${seoulFilenameDate(absoluteTime)}.jpg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const requestExport = async () => {
    if (!exportCameraId) {
      setMessage("내보낼 카메라를 선택하세요.");
      return;
    }
    try {
      const result = await api<{ id: string }>("/api/recordings/exports", {
        method: "POST",
        body: JSON.stringify({ cameraId: exportCameraId, start: fromSeoulInput(exportStart), end: fromSeoulInput(exportEnd) })
      });
      setMessage(`선택한 구간의 내보내기를 시작했습니다. 작업 번호: ${result.id.slice(0, 8)}`);
      await loadExports();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "내보내기 요청 실패");
    }
  };

  const lockSegment = async (segment: TimelineSegment) => {
    await api(`/api/recordings/segments/${segment.id}/lock`, { method: "PATCH", body: JSON.stringify({ locked: !segment.locked }) });
    if (timeline) storeTimeline({ ...timeline, segments: timeline.segments.map((item) => item.id === segment.id ? { ...item, locked: !item.locked } : item) });
  };

  const deleteSegment = async (segment: TimelineSegment) => {
    const range = `${formatSeoul(segment.startAt)} ~ ${formatSeoul(segment.endAt)}`;
    if (!window.confirm(`${range} 녹화 파일을 영구 삭제하시겠습니까?\n삭제한 영상은 복구할 수 없습니다.`)) return;
    const isCurrent = Boolean(absoluteTime && absoluteTime >= new Date(segment.startAt) && absoluteTime < new Date(segment.endAt));
    if (isCurrent) clearPlayback();
    try {
      await api(`/api/recordings/segments/${segment.id}`, { method: "DELETE" });
      await loadTimelineWindow(timelineWindowRef.current);
      setMessage("녹화 파일을 삭제했습니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "녹화 파일 삭제 실패");
    }
  };

  const reportPlaybackEvent = (
    id: string,
    event: "recovery_started" | "recovered",
    reason?: PlaybackRecoveryReason,
    media?: PlaybackMediaDiagnostic,
    suppressedCount = 0,
    durationMs?: number
  ) => {
    const at = currentPlaybackTime();
    if (!at) return;
    void api(`/api/recordings/${id}/playback-event`, {
      method: "POST",
      body: JSON.stringify({ event, reason, at: at.toISOString(), media, suppressedCount, durationMs })
    }).catch(() => undefined);
  };

  const recoverTilePlayback = (id: string, reason: PlaybackRecoveryReason, revision: number, media?: PlaybackMediaDiagnostic) => {
    const sourceAtRecovery = tilePlaybacksRef.current[id];
    if (!isCurrentPlaybackRevision(sourceAtRecovery?.revision, revision) || !sourceAtRecovery) return;
    let synchronizedTime = currentPlaybackTime();
    if (!synchronizedTime) return;
    const existing = sourceAtRecovery;
    if (reason === "ended" && existing?.end && synchronizedTime < existing.end) {
      synchronizedTime = existing.end;
      sharedClockRef.current.timeMs = synchronizedTime.getTime();
      setAbsoluteTime(synchronizedTime);
    }
    // A recording range ending is expected timeline flow, not a playback fault.
    // Keep the existing per-tile retry but only audit real interruption signals.
    if (reason !== "ended" && !tileRecoveringRef.current.has(id)) {
      const auditKey = `${id}:${reason}`;
      const auditInterval = reason === "error" ? errorRecoveryAuditIntervalMs : staleRecoveryAuditIntervalMs;
      const audit = samplePlaybackRecoveryAudit(playbackRecoveryAuditRef.current.get(auditKey), Date.now(), auditInterval);
      playbackRecoveryAuditRef.current.set(auditKey, audit.next);
      tileRecoveringRef.current.set(id, {
        reason,
        reported: audit.report,
        suppressedCount: audit.suppressedCount,
        startedAt: Date.now()
      });
      if (audit.report) reportPlaybackEvent(id, "recovery_started", reason, media, audit.suppressedCount);
    }
    const now = performance.now();
    const retryAfter = 5_000 - (now - (tileRecoveryAtRef.current.get(id) ?? 0));
    if (retryAfter > 0) {
      if (!tileRecoveryTimersRef.current.has(id)) {
        const timer = window.setTimeout(() => {
          tileRecoveryTimersRef.current.delete(id);
          recoverTilePlayback(id, reason, revision);
        }, retryAfter);
        tileRecoveryTimersRef.current.set(id, timer);
      }
      return;
    }
    const pendingTimer = tileRecoveryTimersRef.current.get(id);
    if (pendingTimer !== undefined) window.clearTimeout(pendingTimer);
    tileRecoveryTimersRef.current.delete(id);
    tileRecoveryAtRef.current.set(id, now);
    setTilePlaybacks((current) => {
      const currentTile = current[id];
      if (!isCurrentPlaybackRevision(currentTile?.revision, revision) || !currentTile) return current;
      const preloaded = currentTile?.preload;
      const timeMs = synchronizedTime.getTime();
      const replacement = reason === "ended" && preloaded && timeMs >= preloaded.start.getTime() && timeMs < preloaded.end.getTime()
        ? { cameraId: id, ...preloaded, preload: null }
        : tilePlaybackAt(id, synchronizedTime, nextPlaybackRevision());
      return { ...current, [id]: replacement };
    });
  };

  const finishTileRecovery = (id: string, revision: number) => {
    if (!isCurrentPlaybackRevision(tilePlaybacksRef.current[id]?.revision, revision)) return;
    const pendingTimer = tileRecoveryTimersRef.current.get(id);
    if (pendingTimer !== undefined) window.clearTimeout(pendingTimer);
    tileRecoveryTimersRef.current.delete(id);
    const recovery = tileRecoveringRef.current.get(id);
    if (!recovery) return;
    tileRecoveringRef.current.delete(id);
    if (recovery.reported) {
      reportPlaybackEvent(
        id,
        "recovered",
        recovery.reason,
        undefined,
        recovery.suppressedCount,
        Math.max(0, Date.now() - recovery.startedAt)
      );
    }
  };

  const timelineTicks = Array.from({ length: 7 }, (_, index) => new Date(timelineWindow.start + ((timelineWindow.end - timelineWindow.start) * index / 6)));
  const timelineCrossesDate = seoulDateKey(new Date(timelineWindow.start)) !== seoulDateKey(new Date(timelineWindow.end - 1));
  const timelineLaneHeight = selectedCameras.length > 1 ? 30 : 48;
  const timelineHeight = Math.max(48, timelineLaneHeight * selectedCameras.length);
  const hasTimelineRanges = Object.values(timelinesByCamera).some((item) => item.ranges.length > 0);
  const audioCamera = audioCameraId ? cameras.find((camera) => camera.id === audioCameraId) : undefined;

  return <>
    <div className="page-heading">
      <div><p className="eyebrow">시간별 녹화 내역</p><h2>녹화 영상 재생</h2></div>
    </div>

    <div ref={playerRef} className="panel playback-player">
      <div className={`playback-grid ${user.videoLayout}`}>
        {playbackSlots.map((camera, index) => {
          if (!camera) return <div key={`empty-${index}`} className="playback-stage layout-empty-tile" style={{ aspectRatio: "16 / 9" }} aria-hidden="true" />;
          return <PlaybackTile
            key={`${index}-${camera.id}`}
            camera={camera}
            playback={tilePlaybacks[camera.id]}
            sharedTime={absoluteTime}
            getSharedTime={currentPlaybackTime}
            playing={playing}
            pageVisible={pageVisible}
            rate={rate}
            audioEnabled={canAudio && camera.id === audioCameraId}
            audioSelected={canAudio && camera.id === audioCameraId}
            muted={muted}
            volume={volume}
            showDebug={canDebug}
            debugSegment={canDebug && absoluteTime ? recordingSegmentAt(playbackTimelinesByCameraRef.current[camera.id]?.segments ?? [], absoluteTime) : null}
            onSelectAudio={canAudio ? () => selectAudioCamera(camera.id) : undefined}
            onVideo={(video) => {
              if (video) tileVideoRefs.current.set(camera.id, video);
              else {
                tileVideoRefs.current.delete(camera.id);
                tileMediaOriginsRef.current.delete(camera.id);
              }
            }}
            onMediaOrigin={(originMs) => {
              if (originMs === null) tileMediaOriginsRef.current.delete(camera.id);
              else tileMediaOriginsRef.current.set(camera.id, originMs);
            }}
            onRecover={(reason, revision, media) => recoverTilePlayback(camera.id, reason, revision, media)}
            onPreloadError={(revision) => {
              tilePreloadRetryAtRef.current.set(camera.id, Date.now() + playbackPreloadRetryMs);
              setTilePlaybacks((current) => {
                const existing = current[camera.id];
                if (!existing?.preload || existing.preload.revision !== revision) return current;
                return { ...current, [camera.id]: { ...existing, preload: null } };
              });
            }}
            onRecovered={(revision) => finishTileRecovery(camera.id, revision)}
            onLoaded={(revision) => markPlaybackLoaded(camera.id, revision)}
            onPlaybackStarted={(revision, time) => startSharedClockFromTile(camera.id, revision, time)}
            onPlaybackSuspended={(notice) => suspendPlaybackRef.current(notice)}
          />;
        })}
        {!playbackSlots.length && <div className="empty-state">내 설정에서 녹화 영상을 재생할 카메라를 배치하세요.</div>}
      </div>

      {timeline && <>
        <div className="timeline-shell">
          {timelineHover && <>
            <span className="timeline-hover-label" style={{ left: `clamp(72px, ${timelineHover.position * 100}%, calc(100% - 72px))` }}>{timelineHover.time}</span>
            <span className="timeline-hover-pointer" style={{ left: `${timelineHover.position * 100}%` }} />
          </>}
          <div
            ref={timelineElementRef}
            className={`timeline ${timelineDragging ? "dragging" : ""}`}
            style={{ height: `${timelineHeight}px` }}
            role="button"
            tabIndex={0}
            aria-disabled={playbackLoading}
            aria-label={`${seoulDateKey(new Date(timelineReferenceTime(timelineWindow)))} 녹화 타임라인. 원하는 시각을 클릭하세요.`}
            onClick={selectTimeline}
            onPointerDown={beginTimelineDrag}
            onPointerMove={moveTimelineDrag}
            onPointerUp={finishTimelineDrag}
            onPointerCancel={finishTimelineDrag}
            onMouseMove={(event) => {
              const point = timelinePoint(event.currentTarget, event.clientX);
              if (point) setTimelineHover({ position: point.position, time: formatTimelineHover(point.time) });
            }}
            onMouseLeave={() => setTimelineHover(null)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                void jumpToTime();
              }
            }}
          >
            {selectedCameras.map((camera, index) => {
              const cameraTimeline = timelinesByCamera[camera.id];
              return <span key={camera.id} className="timeline-camera-lane" style={{ top: `${index * timelineLaneHeight}px`, height: `${timelineLaneHeight}px` }}>
                {selectedCameras.length > 1 && <span className="timeline-camera-lane-label">{String(camera.position).padStart(2, "0")} · {camera.name}</span>}
                {cameraTimeline?.ranges.map((range) => {
                  const placement = rangePlacement(range, timelineWindow);
                  return placement ? <span key={range.startAt} className="timeline-range" style={placement} /> : null;
                })}
                {cameraTimeline?.motionRanges.map((range) => {
                  const placement = rangePlacement(range, timelineWindow);
                  return placement ? <span key={`${range.startAt}-${range.endAt}`} className="timeline-motion-range" style={placement} /> : null;
                })}
              </span>;
            })}
            {absoluteTime && absoluteTime.getTime() >= timelineWindow.start && absoluteTime.getTime() < timelineWindow.end && <span
              className="timeline-cursor"
              style={{ left: `${((absoluteTime.getTime() - timelineWindow.start) / (timelineWindow.end - timelineWindow.start)) * 100}%` }}
            />}
            {timelineHover && <span className="timeline-hover-line" style={{ left: `${timelineHover.position * 100}%` }} />}
          </div>
        </div>
        <div className="timeline-hours">{timelineTicks.map((tick, index) => <span key={`${tick.toISOString()}-${index}`}>{formatTimelineTick(tick, timelineTicks[index - 1], timelineCrossesDate)}</span>)}</div>
        <div className="timeline-toolbar mt-2">
          <div className="btn-group btn-group-sm" aria-label="타임라인 확대 및 축소">
            <button type="button" className="btn btn-outline-secondary" disabled={timelineZoomLevel === 0} onClick={() => zoomTimeline(-1)} aria-label="타임라인 축소"><i className="bi bi-dash-lg" /></button>
            <button type="button" className="btn btn-outline-secondary" disabled={timelineZoomLevel === timelineZoomDurations.length - 1} onClick={() => zoomTimeline(1)} aria-label="타임라인 확대"><i className="bi bi-plus-lg" /></button>
          </div>
          <div className="playback-meta"><span className="timeline-legend recorded" />녹화 <span className="timeline-legend motion" />움직임 · 클릭하여 재생, 좌우 드래그로 날짜·시간 이동, 마우스 휠로 확대·축소합니다.</div>
        </div>

        <div className="playback-command-panel">
          {absoluteTime && <>
            <div className="playback-primary-controls">
              <div className="playback-control-group playback-transport-controls">
                <button type="button" className={`btn btn-sm ${playing ? "btn-outline-secondary" : "btn-primary"}`} onClick={play} disabled={playing}><i className="bi bi-play-fill me-1" />재생</button>
                <button type="button" className={`btn btn-sm ${playing ? "btn-primary" : "btn-outline-secondary"}`} onClick={pause} disabled={!playing}><i className="bi bi-pause-fill me-1" />일시정지</button>
                <button type="button" className="btn btn-sm btn-outline-light" onClick={stop} disabled={!playing}><i className="bi bi-stop-fill me-1" />정지</button>
              </div>
              <div className="playback-secondary-controls">
                <span className="playback-meta">재생 속도</span>
                <div className="rate-controls">{playbackRates.map((value) => <button key={value} type="button" className={`btn btn-sm ${rate === value ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => changeRate(value)}>{value}x</button>)}</div>
                <div className="playback-control-group playback-permission-controls">
                  {canAudio && <><span className="playback-meta">오디오 · {audioCamera?.name ?? "카메라 선택 필요"}</span><button type="button" className="btn btn-sm btn-outline-light" onClick={toggleMute} disabled={!audioCameraId} aria-label={muted ? "음소거 해제" : "음소거"}><i className={`bi bi-volume-${muted || volume === 0 ? "mute" : volume < 0.5 ? "down" : "up"}-fill`} /></button><input type="range" className="playback-volume" min="0" max="1" step="0.05" value={muted ? 0 : volume} disabled={!audioCameraId} onChange={(event) => changeVolume(Number(event.target.value))} aria-label="음량" /></>}
                  {canSnapshot && <button type="button" className="btn btn-sm btn-outline-light" onClick={() => void snapshot()}><i className="bi bi-camera me-1" />현재 화면 저장</button>}
                  <button type="button" className="btn btn-sm btn-outline-light" onClick={() => void playerRef.current?.requestFullscreen()}><i className="bi bi-fullscreen me-1" />전체화면</button>
                </div>
              </div>
            </div>

            <hr className="playback-control-divider" />

            <div className="playback-seek-controls">
              <div className="playback-seek-group">
                <span className="playback-meta">이전 시간</span>
                <div className="playback-control-group playback-navigation-controls">{seekSteps.map((step) => <button key={`back-${step.seconds}`} type="button" className="btn btn-sm btn-outline-light" disabled={playbackLoading} onClick={() => seekBy(-step.seconds)}>-{step.label}</button>)}</div>
              </div>
              <div className="playback-seek-group">
                <span className="playback-meta">이후 시간</span>
                <div className="playback-control-group playback-navigation-controls">{seekSteps.map((step) => <button key={`forward-${step.seconds}`} type="button" className="btn btn-sm btn-outline-light" disabled={playbackLoading} onClick={() => seekBy(step.seconds)}>+{step.label}</button>)}</div>
              </div>
            </div>

            <hr className="playback-control-divider" />
          </>}

          <div className="jump-controls">
            <div><label htmlFor="playback-jump-at">날짜·시각 직접 이동</label><input id="playback-jump-at" type="datetime-local" step="1" className="form-control" value={jumpAt} onChange={(event) => setJumpAt(event.target.value)} /></div>
            <button type="button" className="btn btn-primary" onClick={jumpToTime} disabled={!hasTimelineRanges || playbackLoading}>해당 시각 재생</button>
          </div>
        </div>

      </>}
      {message && <div className="playback-message small text-secondary mt-3" role="status">{message}</div>}
    </div>

    {canDownload && <div className="panel mt-4">
      <div className="panel-title"><h3>선택 구간 내보내기</h3><span>한 번에 최대 24시간 · MP4 파일</span></div>
      <div className="row g-3 align-items-end">
        <div className="col-md-3"><label htmlFor="export-camera">카메라</label><select id="export-camera" className="form-select" value={exportCameraId} onChange={(event) => setExportCameraId(event.target.value)}>
          {cameras.filter((camera) => camera.enabled).map((camera) => <option key={camera.id} value={camera.id}>{String(camera.position).padStart(2, "0")} · {camera.name}</option>)}
        </select></div>
        <div className="col-md-3"><label>시작 시각 (한국 시간)</label><input type="datetime-local" step="1" className="form-control" value={exportStart} onChange={(event) => setExportStart(event.target.value)} /></div>
        <div className="col-md-3"><label>종료 시각 (한국 시간)</label><input type="datetime-local" step="1" className="form-control" value={exportEnd} onChange={(event) => setExportEnd(event.target.value)} /></div>
        <div className="col-md-3"><button type="button" className="btn btn-primary w-100" disabled={!exportCameraId} onClick={() => void requestExport()}>내보내기</button></div>
      </div>
      {exports.length > 0 && <div className="table-responsive mt-4"><table className="table table-dark align-middle mb-0"><thead><tr><th>카메라 / 구간</th><th>상태</th><th>크기</th><th /></tr></thead><tbody>{exports.map((job) => <tr key={job.id}>
        <td>{job.cameraName}<div className="small text-secondary">{formatSeoul(job.startAt)} ~ {formatSeoul(job.endAt)}</div></td>
        <td><div className="progress export-progress"><div className={`progress-bar ${job.status === "failed" ? "bg-danger" : ""}`} style={{ width: `${job.status === "failed" ? 100 : job.progress}%` }} /></div><div className="small text-secondary">{exportStatus(job.status)} {job.progress}% {job.error ?? ""}</div></td>
        <td>{job.fileSize ? formatBytes(job.fileSize) : "-"}</td><td className="text-end">{job.status === "completed" && <a className="btn btn-sm btn-success" href={`/api/recordings/exports/${job.id}/download`}>다운로드</a>}</td>
      </tr>)}</tbody></table></div>}
    </div>}

    {(canConfigure || canDelete) && visibleSegments.length > 0 && <details className="panel mt-4">
      <summary>현재 범위 녹화 파일 상세 정보{canConfigure ? " / 자동 삭제 방지" : ""}</summary><div className="table-responsive mt-3"><table className="table table-dark align-middle mb-0"><thead><tr><th>시작</th><th>종료</th><th>영상 형식</th><th>크기</th>{canConfigure && <th>삭제 방지</th>}{canDelete && <th className="text-end">삭제</th>}</tr></thead><tbody>{visibleSegments.map((segment) => <tr key={segment.id}>
        <td>{formatSeoul(segment.startAt)}</td><td>{formatSeoul(segment.endAt)}</td><td>{segment.codec} {segment.resolution}</td><td>{formatBytes(segment.fileSize)}</td>{canConfigure && <td><button type="button" className={`btn btn-sm ${segment.locked ? "btn-warning" : "btn-outline-secondary"}`} onClick={() => void lockSegment(segment)}><i className={`bi bi-${segment.locked ? "lock-fill" : "unlock"}`} /></button></td>}{canDelete && <td className="text-end"><button type="button" className="btn btn-sm btn-outline-danger" disabled={segment.locked} title={segment.locked ? "잠금을 해제한 뒤 삭제할 수 있습니다." : "이 녹화 파일 영구 삭제"} onClick={() => void deleteSegment(segment)}><i className="bi bi-trash me-1" />삭제</button></td>}
      </tr>)}</tbody></table></div>
    </details>}
  </>;
}

function PlaybackTile({ camera, playback, sharedTime, getSharedTime, playing, pageVisible, rate, audioEnabled, audioSelected, muted, volume, showDebug, debugSegment, onVideo, onMediaOrigin, onRecover, onPreloadError, onRecovered, onLoaded, onPlaybackStarted, onPlaybackSuspended, onSelectAudio }: {
  camera: CameraSummary;
  playback: TilePlayback | undefined;
  sharedTime: Date | null;
  getSharedTime: () => Date | null;
  playing: boolean;
  pageVisible: boolean;
  rate: number;
  audioEnabled: boolean;
  audioSelected: boolean;
  muted: boolean;
  volume: number;
  showDebug: boolean;
  debugSegment: TimelineSegment | null;
  onVideo: (video: HTMLVideoElement | null) => void;
  onMediaOrigin: (originMs: number | null) => void;
  onRecover: (reason: PlaybackRecoveryReason, revision: number, media?: PlaybackMediaDiagnostic) => void;
  onPreloadError: (revision: number) => void;
  onRecovered: (revision: number) => void;
  onLoaded: (revision: number) => void;
  onPlaybackStarted: (revision: number, time: Date) => void;
  onPlaybackSuspended: (notice: string) => void;
  onSelectAudio?: () => void;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mseVideoRef = useRef<HTMLVideoElement | null>(null);
  const mseSessionRef = useRef<RecordedMseSession | null>(null);
  const mseDisabledRef = useRef(false);
  const playbackRef = useRef(playback);
  const onVideoRef = useRef(onVideo);
  const onMediaOriginRef = useRef(onMediaOrigin);
  const getSharedTimeRef = useRef(getSharedTime);
  const onRecoverRef = useRef(onRecover);
  const onRecoveredRef = useRef(onRecovered);
  const onPlaybackSuspendedRef = useRef(onPlaybackSuspended);
  const [readySources, setReadySources] = useState<string[]>([]);
  const [mseMode, setMseMode] = useState<"probing" | "active" | "fallback">("probing");
  const [mseReady, setMseReady] = useState(false);
  const [mseOriginMs, setMseOriginMs] = useState<number | null>(null);
  const [mseSessionVersion, setMseSessionVersion] = useState(0);
  const [aspectRatio, setAspectRatio] = useState("16 / 9");
  const [decodedStats, setDecodedStats] = useState<{ width: number; height: number; fps: number | null; droppedFrames: number } | null>(null);
  const lastProgressRef = useRef({ observedAt: performance.now(), mediaTime: -1 });
  const recoveryReportedRef = useRef(false);
  const hasPlayedRef = useRef(false);
  const seekStartedAtRef = useRef<number | null>(null);
  const lastDirectSeekRef = useRef<DirectPlaybackSeekState | null>(null);
  const localRecoveryRef = useRef<LocalPlaybackRecovery | null>(null);
  const playAttemptPendingRef = useRef(false);
  const zoom = useAreaZoom({ onTap: onSelectAudio });
  const sharedMs = sharedTime?.getTime() ?? null;
  const startMs = playback?.start?.getTime() ?? null;
  const endMs = playback?.end?.getTime() ?? null;
  const nativeOnly = Boolean(playback?.url && playbackUrlUsesRepresentation(playback.url, "native"));
  const usingMse = !nativeOnly && mseMode !== "fallback";
  const directPlaybackUrl = playback?.url ? playbackUrlWithRepresentation(playback.url, "native") : "";
  const directPreloadUrl = playback?.preload ? playbackUrlWithRepresentation(playback.preload.url, "native") : "";
  const playbackKey = playback?.url
    ? `${usingMse ? playback.url : directPlaybackUrl}-${playback.revision}`
    : "";
  const preloadKey = playback?.preload
    ? `${usingMse ? playback.preload.url : directPreloadUrl}-${playback.preload.revision}`
    : "";
  const mediaKey = usingMse ? `mse-${mseSessionVersion}` : playbackKey;
  const activeSourceKeysRef = useRef(new Set<string>());
  activeSourceKeysRef.current = new Set([playbackKey, preloadKey].filter(Boolean));
  const ready = usingMse ? mseReady : Boolean(playbackKey && readySources.includes(playbackKey));
  const directActive = sharedMs !== null && startMs !== null && endMs !== null && sharedMs >= startMs && sharedMs < endMs;
  const directHandoffReady = Boolean(preloadKey && readySources.includes(preloadKey));
  // A MediaSource remains continuous across logical 30-second chunks. Do not
  // hide it during the render in which the parent promotes a preloaded chunk.
  // Direct fallback also keeps its final frame for that render when the next
  // element already has decoded data, avoiding a guaranteed black paint.
  const active = usingMse ? Boolean(playback?.url && sharedMs !== null) : directActive || directHandoffReady;
  const mediaStartMs = usingMse ? mseOriginMs : startMs;
  playbackRef.current = playback;
  onVideoRef.current = onVideo;
  onMediaOriginRef.current = onMediaOrigin;
  getSharedTimeRef.current = getSharedTime;
  onRecoverRef.current = onRecover;
  onRecoveredRef.current = onRecovered;
  onPlaybackSuspendedRef.current = onPlaybackSuspended;
  const setVideoRef = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video;
    onVideoRef.current(video);
  }, []);
  const setMseVideoRef = useCallback((video: HTMLVideoElement | null) => {
    mseVideoRef.current = video;
    videoRef.current = video;
    onVideoRef.current(video);
  }, []);

  useEffect(() => {
    const current = playback?.url && playback.start && playback.end
      ? playbackChunk(playback.url, playback.start, playback.end)
      : null;
    if (!current) {
      mseSessionRef.current?.destroy();
      mseSessionRef.current = null;
      setMseReady(false);
      setMseOriginMs(null);
      onMediaOriginRef.current(null);
      return;
    }
    if (nativeOnly) {
      mseSessionRef.current?.destroy();
      mseSessionRef.current = null;
      setMseReady(false);
      setMseOriginMs(null);
      setMseMode("probing");
      onMediaOriginRef.current(current.startMs);
      return;
    }
    if (mseDisabledRef.current) {
      setMseMode("fallback");
      onMediaOriginRef.current(current.startMs);
      return;
    }
    const video = mseVideoRef.current;
    if (!video) return;

    let session = mseSessionRef.current;
    if (!session || (!session.has(current.url) && !session.canContinueWith(current))) {
      session?.destroy();
      let opened = false;
      setMseMode("probing");
      setMseReady(false);
      setMseOriginMs(current.startMs);
      onMediaOriginRef.current(current.startMs);
      setMseSessionVersion((value) => value + 1);
      const created = new RecordedMseSession(video, current, {
        getDesiredTime: (originMs) => {
          const desired = getSharedTimeRef.current();
          return desired ? Math.max(0, (desired.getTime() - originMs) / 1_000) : null;
        },
        onOpen: ({ originMs }) => {
          if (mseSessionRef.current !== created) return;
          opened = true;
          setMseOriginMs(originMs);
          setMseMode("active");
          onMediaOriginRef.current(originMs);
        },
        onAppend: ({ chunk, appendedSeconds }) => {
          if (mseSessionRef.current !== created || appendedSeconds === null) return;
          const expectedSeconds = Math.max(0, (chunk.endMs - chunk.startMs) / 1000);
          // A very short append would make the shared absolute clock outrun the
          // MSE buffer. Let the current damaged chunk advance to the following
          // recording without disabling MSE for the entire tile.
          if (isSeverelyShortMseChunk(appendedSeconds, expectedSeconds)) {
            throw new MseChunkDurationMismatchError(chunk.url, appendedSeconds, expectedSeconds);
          }
        },
        onError: (error) => {
          if (mseSessionRef.current !== created) return;
          created.destroy();
          mseSessionRef.current = null;
          const latest = playbackRef.current;
          if (error instanceof MseChunkDurationMismatchError && opened && latest?.url === error.chunkUrl) {
            setMseReady(false);
            setMseMode("probing");
            onMediaOriginRef.current(latest.start?.getTime() ?? null);
            window.setTimeout(() => onRecoverRef.current(
              "ended",
              latest.revision,
              playbackMediaDiagnostic(video, "ended")
            ), 0);
            return;
          }
          mseDisabledRef.current = true;
          setMseReady(false);
          setMseMode("fallback");
          onMediaOriginRef.current(latest?.start?.getTime() ?? null);
          if (opened && latest) {
            const diagnostic = playbackMediaDiagnostic(video, "media_error");
            diagnostic.errorMessage = error.message.slice(0, 500);
            window.setTimeout(() => onRecoverRef.current("error", latest.revision, diagnostic), 0);
          } else if (!(error instanceof MsePlaybackUnsupportedError)) {
            // Initial append errors are expected to be browser-specific. The
            // direct player starts immediately without recording a false outage.
            console.warn("MSE playback unavailable; using direct playback", error);
          }
        }
      });
      mseSessionRef.current = created;
      session = created;
    } else if (!session.has(current.url)) {
      session.enqueue(current);
    }

    const preload = playback?.preload;
    const next = preload
      ? playbackChunk(preload.url, preload.start, preload.end)
      : null;
    if (next && !session.has(next.url) && session.canContinueWith(next)) session.enqueue(next);
  }, [nativeOnly, playback?.url, playback?.start, playback?.end, playback?.preload?.url, playback?.preload?.start, playback?.preload?.end]);

  useEffect(() => () => {
    mseSessionRef.current?.destroy();
    mseSessionRef.current = null;
    onMediaOriginRef.current(null);
  }, []);

  useEffect(() => {
    setReadySources((current) => current.filter((key) => key === playbackKey || key === preloadKey));
  }, [playbackKey, preloadKey]);

  useEffect(() => {
    lastProgressRef.current = { observedAt: performance.now(), mediaTime: -1 };
    recoveryReportedRef.current = false;
    hasPlayedRef.current = false;
    seekStartedAtRef.current = null;
    lastDirectSeekRef.current = null;
    localRecoveryRef.current = null;
    playAttemptPendingRef.current = false;
    zoom.resetZoom();
  }, [mediaKey, zoom.resetZoom]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    return () => {
      // React can retain a keyed preload element when promoting it to current.
      // Abort only sources that disappeared from the next render, so obsolete
      // seek/preload requests cannot keep MediaMTX and the HDD busy.
      stage?.querySelectorAll<HTMLVideoElement>("video.playback-video").forEach((video) => {
        if (video.dataset.playbackMse === "true") return;
        const key = video.dataset.playbackSourceKey ?? "";
        if (!activeSourceKeysRef.current.has(key)) releaseVideoElement(video);
      });
    };
  }, [playbackKey, preloadKey]);

  useEffect(() => {
    if (!showDebug) {
      setDecodedStats(null);
      return;
    }
    let previous: { at: number; frames: number } | null = null;
    const collect = () => {
      const video = videoRef.current;
      if (!video) return;
      const quality = video.getVideoPlaybackQuality();
      const now = performance.now();
      const fps = previous && now > previous.at ? Math.max(0, (quality.totalVideoFrames - previous.frames) * 1000 / (now - previous.at)) : null;
      previous = { at: now, frames: quality.totalVideoFrames };
      setDecodedStats({ width: video.videoWidth, height: video.videoHeight, fps, droppedFrames: quality.droppedVideoFrames });
    };
    collect();
    const timer = window.setInterval(collect, 2_000);
    return () => window.clearInterval(timer);
  }, [mediaKey, showDebug]);

  const sourceResolution = parseResolution(debugSegment?.resolution);
  const debugStats: MediaDebugStats = {
    source: usingMse ? "PLAYBACK MSE" : "PLAYBACK",
    codec: debugSegment?.codec,
    width: decodedStats?.width || sourceResolution?.width || null,
    height: decodedStats?.height || sourceResolution?.height || null,
    fps: decodedStats?.fps ?? debugSegment?.fps ?? null,
    bitrate: debugSegment?.durationMs ? debugSegment.fileSize * 8_000 / debugSegment.durationMs : null,
    droppedFrames: decodedStats?.droppedFrames ?? null
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!pageVisible) {
      playAttemptPendingRef.current = false;
      video?.pause();
      return;
    }
    if (!video || sharedMs === null || mediaStartMs === null || !active) {
      video?.pause();
      return;
    }
    const desired = Math.max(0, (sharedMs - mediaStartMs) / 1000);
    if (Number.isFinite(video.duration) && desired >= video.duration) {
      video.pause();
      return;
    }
    if (!ready) {
      // appendWindowStart deliberately drops the beginning of a recording.
      // Seek into the retained range before loadeddata; otherwise currentTime
      // can remain at zero forever even though the desired video is buffered.
      const startupTarget = usingMse ? mseStartupSeekTarget(video, desired) : null;
      if (startupTarget !== null) {
        video.currentTime = startupTarget;
        video.playbackRate = rate;
      }
      video.pause();
      return;
    }
    const drift = desired - video.currentTime;
    const observedAt = performance.now();
    if (usingMse && Math.abs(drift) > 0.75 && bufferedTimeIncludes(video, desired)) {
      // MSE seeking stays inside the existing SourceBuffer and does not reopen
      // an HTTP resource, so a late camera can join the shared clock at once.
      video.currentTime = desired;
      video.playbackRate = rate;
    } else if (!usingMse && !video.seeking
      && shouldSeekDirectPlayback(lastDirectSeekRef.current, mediaKey, observedAt, drift)
      && video.readyState >= HTMLMediaElement.HAVE_METADATA
      && (!Number.isFinite(video.duration) || desired < video.duration)) {
      // Native playback may issue byte ranges for a seek. Never seek backward:
      // a decoder that cannot sustain an accelerated rate catches up at a
      // bounded interval instead of replaying an older scene or reloading the
      // complete resource after it falls far behind.
      lastDirectSeekRef.current = { mediaKey, at: observedAt };
      video.currentTime = desired;
      video.playbackRate = rate;
    } else {
      const driftRatio = Math.min(drift > 0 ? 0.25 : 0.2, Math.abs(drift) / 20);
      // Keep small clock differences smooth after either MSE or direct seeking
      // has placed the tile near the shared absolute time.
      const correction = Math.abs(drift) > 0.35
        ? Math.min(16, Math.max(0.25, rate * (drift > 0 ? 1 + driftRatio : 1 - driftRatio)))
        : rate;
      video.playbackRate = correction;
    }
    video.volume = volume;
    video.muted = !audioEnabled || muted;
    if (playing) {
      if (video.paused && !playAttemptPendingRef.current) {
        playAttemptPendingRef.current = true;
        void video.play().then(() => {
          playAttemptPendingRef.current = false;
        }).catch((error: unknown) => {
          playAttemptPendingRef.current = false;
          if (isBenignPlayInterruption(error)) {
            // React/MSE source promotion deliberately pauses the old element.
            // Chrome rejects its still-pending play() promise even though no
            // media failure occurred; the next clock render resumes playback.
            lastProgressRef.current = { observedAt: performance.now(), mediaTime: video.currentTime };
            return;
          }
          if (document.visibilityState !== "visible" || isBackgroundMediaInterruption(error)) {
            lastProgressRef.current = { observedAt: performance.now(), mediaTime: video.currentTime };
            onPlaybackSuspendedRef.current("브라우저 절전 정책이 백그라운드 영상을 중지하여 재생을 일시정지했습니다. 재생 버튼을 누르면 현재 위치에서 계속됩니다.");
            return;
          }
          if (recoveryReportedRef.current || !playback) return;
          recoveryReportedRef.current = true;
          const diagnostic = playbackMediaDiagnostic(video, "play_rejected");
          diagnostic.errorMessage = error instanceof Error ? error.message.slice(0, 500) : String(error).slice(0, 500);
          onRecoverRef.current("error", playback.revision, diagnostic);
        });
      }
    } else {
      playAttemptPendingRef.current = false;
      video.pause();
    }
  }, [active, audioEnabled, mediaStartMs, muted, pageVisible, playback, playing, rate, ready, sharedMs, usingMse, volume]);

  const markProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const currentTime = video.currentTime;
    const previous = lastProgressRef.current;
    if (previous.mediaTime < 0 || Math.abs(currentTime - previous.mediaTime) >= 0.02) {
      lastProgressRef.current = { observedAt: performance.now(), mediaTime: currentTime };
      recoveryReportedRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Initial loading is not a stale playback. Recovery begins only after this
    // exact video revision has actually reached the playing state once.
    if (!pageVisible || !active || !playing || !playback?.url || !ready) return;
    if (ready) markProgress();
    const timer = window.setInterval(() => {
      if (recoveryReportedRef.current) return;
      if (document.visibilityState !== "visible") return;
      const now = performance.now();
      const video = videoRef.current;
      const shared = getSharedTimeRef.current();
      if (!video || !shared || mediaStartMs === null) return;
      if (!hasPlayedRef.current) return;
      const localRecovery = localRecoveryRef.current;
      if (localRecovery?.mediaKey === mediaKey) {
        if (now - localRecovery.startedAt < localPlaybackRecoveryValidationMs) return;
        localRecoveryRef.current = null;
        if (video.currentTime >= localRecovery.targetTime + localPlaybackRecoveryMinimumAdvanceSeconds) {
          lastProgressRef.current = { observedAt: now, mediaTime: video.currentTime };
          seekStartedAtRef.current = null;
          return;
        }
        recoveryReportedRef.current = true;
        const desired = Math.max(0, (shared.getTime() - mediaStartMs) / 1000);
        onRecoverRef.current("stale", playback.revision, playbackMediaDiagnostic(video, "stale_timeout", desired));
        return;
      }
      if (video.seeking) {
        seekStartedAtRef.current ??= now;
        if (now - seekStartedAtRef.current < playbackStaleTimeoutMs) return;
      }
      const stale = now - lastProgressRef.current.observedAt >= playbackStaleTimeoutMs;
      if (!stale) return;
      const desired = Math.max(0, (shared.getTime() - mediaStartMs) / 1000);
      const localTarget = usingMse
        ? bufferedPlaybackRecoveryTarget(video, desired)
        : directPlaybackRecoveryTarget(video, desired);
      if (localTarget !== null) {
        localRecoveryRef.current = { mediaKey, startedAt: now, targetTime: localTarget };
        if (!usingMse) lastDirectSeekRef.current = { mediaKey, at: now };
        seekStartedAtRef.current = now;
        video.currentTime = localTarget;
        video.playbackRate = rate;
        if (video.paused && !playAttemptPendingRef.current) {
          playAttemptPendingRef.current = true;
          void video.play().catch(() => undefined).finally(() => {
            playAttemptPendingRef.current = false;
          });
        }
        return;
      }
      recoveryReportedRef.current = true;
      const atExpectedEnd = usingMse && endMs !== null && !playback.preload
        && mediaStartMs + video.currentTime * 1000 + playbackEndToleranceSeconds * 1000 >= endMs;
      onRecoverRef.current(
        atExpectedEnd ? "ended" : "stale",
        playback.revision,
        playbackMediaDiagnostic(video, atExpectedEnd ? "ended" : "stale_timeout", desired)
      );
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [active, endMs, markProgress, mediaKey, mediaStartMs, pageVisible, playing, rate, ready, playback?.preload, playback?.revision, playback?.url, usingMse]);

  useEffect(() => {
    if (!pageVisible || !active || !playing || !playback?.url || ready) return;
    const timer = window.setTimeout(() => {
      if (!recoveryReportedRef.current) {
        recoveryReportedRef.current = true;
        const video = videoRef.current;
        onRecoverRef.current("error", playback.revision, playbackMediaDiagnostic(video, "load_timeout"));
      }
    }, playbackStaleTimeoutMs);
    return () => window.clearTimeout(timer);
  }, [active, pageVisible, playback?.url, playback?.revision, playing, ready]);

  const finishMseChunkAtBufferEnd = (video: HTMLVideoElement) => {
    const latest = playbackRef.current;
    if (!pageVisible || !playing || !latest?.end || latest.preload || mseOriginMs === null || recoveryReportedRef.current) return;
    const absoluteMediaMs = mseOriginMs + video.currentTime * 1000;
    if (absoluteMediaMs + playbackEndToleranceSeconds * 1000 < latest.end.getTime()) return;
    recoveryReportedRef.current = true;
    onRecoverRef.current("ended", latest.revision, playbackMediaDiagnostic(video, "ended"));
  };

  return <div
    ref={stageRef}
    className={`playback-stage zoom-surface${audioSelected ? " playback-audio-selected" : ""}`}
    style={{ aspectRatio }}
    {...zoom.surfaceProps}
    role={onSelectAudio ? "button" : undefined}
    tabIndex={onSelectAudio ? 0 : undefined}
    aria-label={onSelectAudio ? `${camera.name} · ${audioSelected ? "오디오 선택됨" : "클릭하여 오디오 선택"}` : undefined}
    onKeyDown={(event) => {
      if (onSelectAudio && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onSelectAudio();
      }
    }}
  >
    <span className="playback-camera-label">{camera.name}</span>
    {audioSelected && <span className="playback-audio-indicator" title={muted || volume === 0 ? "이 카메라의 오디오가 선택되었으며 현재 음소거 상태입니다." : "이 카메라의 오디오가 선택되었습니다."}><i className={`bi bi-volume-${muted || volume === 0 ? "mute" : "up"}-fill`} /></span>}
    {showDebug && <MediaDebugOverlay stats={debugStats} />}
    {playback?.url ? <>
      {usingMse ? <video
        key="mse"
        ref={setMseVideoRef}
        className={`playback-video${active ? "" : " playback-video-hidden"}`}
        data-playback-mse="true"
        style={zoom.videoStyle}
        playsInline
        preload="auto"
        muted={!audioEnabled || muted}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          playAttemptPendingRef.current = false;
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            setAspectRatio(`${Math.max(16 / 9, video.videoWidth / video.videoHeight)}`);
          }
          video.playbackRate = rate;
          video.volume = volume;
          video.muted = !audioEnabled || muted;
        }}
        onLoadedData={() => {
          const latest = playbackRef.current;
          setMseReady(true);
          markProgress();
          if (!playing && latest) onLoaded(latest.revision);
        }}
        onPlaying={(event) => {
          const latest = playbackRef.current;
          if (!latest || mseOriginMs === null) return;
          const video = event.currentTarget;
          setMseReady(true);
          hasPlayedRef.current = true;
          seekStartedAtRef.current = null;
          markProgress();
          onLoaded(latest.revision);
          onPlaybackStarted(latest.revision, new Date(mseOriginMs + video.currentTime * 1000));
          onRecoveredRef.current(latest.revision);
        }}
        onSeeking={() => { seekStartedAtRef.current ??= performance.now(); }}
        onSeeked={() => { seekStartedAtRef.current = null; markProgress(); }}
        onTimeUpdate={markProgress}
        onWaiting={(event) => finishMseChunkAtBufferEnd(event.currentTarget)}
        onEnded={(event) => finishMseChunkAtBufferEnd(event.currentTarget)}
        onError={(event) => {
          if (event.currentTarget.dataset.nvrReleasing === "true") return;
          const latest = playbackRef.current;
          mseSessionRef.current?.destroy();
          mseSessionRef.current = null;
          mseDisabledRef.current = true;
          setMseReady(false);
          setMseMode("fallback");
          onMediaOriginRef.current(latest?.start?.getTime() ?? null);
          if (!latest) return;
          onLoaded(latest.revision);
          onRecover("error", latest.revision, playbackMediaDiagnostic(event.currentTarget, "media_error"));
        }}
      /> : [
        { source: { start: playback.start!, end: playback.end!, url: directPlaybackUrl, revision: playback.revision }, current: true },
        ...(playback.preload ? [{ source: { ...playback.preload, url: directPreloadUrl }, current: false }] : [])
      ].map(({ source, current }) => {
        const sourceKey = `${source.url}-${source.revision}`;
        return <video
          key={sourceKey}
          ref={current ? setVideoRef : undefined}
          className={`playback-video${current ? active ? "" : " playback-video-hidden" : " playback-video-preload"}`}
          data-playback-source-key={sourceKey}
          style={current ? zoom.videoStyle : undefined}
          src={source.url}
          playsInline
          preload="auto"
          muted={current ? !audioEnabled || muted : true}
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;
            playAttemptPendingRef.current = false;
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              // 세로형 영상이 분면과 그리드 행 자체를 늘리지 않도록 초기 16:9 높이를 상한으로 둔다.
              // 영상은 object-fit: contain으로 분면 안에 표시되므로 좁은 원본에는 좌우 레터박스가 생긴다.
              setAspectRatio(`${Math.max(16 / 9, video.videoWidth / video.videoHeight)}`);
            }
            video.playbackRate = rate;
            video.volume = volume;
            video.muted = current ? !audioEnabled || muted : true;
          }}
          onLoadedData={() => {
            setReadySources((keys) => keys.includes(sourceKey) ? keys : [...keys, sourceKey]);
            if (!current) return;
            markProgress();
            if (!playing) onLoaded(source.revision);
          }}
          onPlaying={(event) => {
            if (!current) return;
            const video = event.currentTarget;
            hasPlayedRef.current = true;
            seekStartedAtRef.current = null;
            markProgress();
            onLoaded(source.revision);
            onPlaybackStarted(source.revision, new Date(source.start.getTime() + video.currentTime * 1000));
            onRecoveredRef.current(source.revision);
          }}
          onSeeking={() => { if (current) seekStartedAtRef.current ??= performance.now(); }}
          onSeeked={() => { if (current) { seekStartedAtRef.current = null; markProgress(); } }}
          onTimeUpdate={() => { if (current) markProgress(); }}
          onEnded={(event) => {
            if (!current || !pageVisible || !playing) return;
            const expectedDuration = Math.max(0, (source.end.getTime() - source.start.getTime()) / 1000);
            if (shouldIgnoreDirectPlaybackEnded(
              event.currentTarget.dataset.nvrReleasing === "true",
              event.currentTarget.currentTime,
              expectedDuration
            )) return;
            const endedEarly = event.currentTarget.currentTime + playbackEndToleranceSeconds < expectedDuration;
            onRecover(endedEarly ? "error" : "ended", source.revision, playbackMediaDiagnostic(event.currentTarget, "ended"));
          }}
          onError={(event) => {
            if (event.currentTarget.dataset.nvrReleasing === "true") return;
            if (!current) {
              onPreloadError(source.revision);
              releaseVideoElement(event.currentTarget);
              return;
            }
            if (!pageVisible) return;
            onLoaded(source.revision);
            onRecover("error", source.revision, playbackMediaDiagnostic(event.currentTarget, "media_error"));
          }}
        />;
      })}
      {!active && <div className="playback-tile-empty" aria-hidden="true" />}
      {active && !ready && <div className="playback-tile-message">영상을 불러오는 중입니다…</div>}
      {active && zoom.selection && <span className="zoom-selection" style={selectionStyle(zoom.selection)} />}
    </> : sharedTime ? <div className="playback-tile-empty" aria-hidden="true" /> : <div className="playback-tile-message">아래 타임라인에서 재생할 시각을 선택하세요.</div>}
  </div>;
}

function recordingSegmentAt(segments: TimelineSegment[], target: Date): TimelineSegment | null {
  const targetMs = target.getTime();
  return segments.find((segment) => targetMs >= new Date(segment.startAt).getTime() && targetMs < new Date(segment.endAt).getTime()) ?? null;
}

export function playbackSegmentAt(segments: TimelineSegment[], target: Date, maximumGapMs = 2_000): TimelineSegment | null {
  const targetMs = target.getTime();
  let containing: TimelineSegment | null = null;
  for (const segment of segments) {
    const start = new Date(segment.startAt).getTime();
    const end = new Date(segment.endAt).getTime();
    if (targetMs >= start && targetMs < end) containing = segment;
  }
  if (containing) return containing;
  return segments.find((segment) => {
    const start = new Date(segment.startAt).getTime();
    return start > targetMs && start - targetMs <= maximumGapMs;
  }) ?? null;
}

function playbackChunk(url: string, start: Date, end: Date): MsePlaybackChunk {
  return { url, startMs: start.getTime(), endMs: end.getTime() };
}

export interface DirectPlaybackSeekState {
  mediaKey: string;
  at: number;
}

export function shouldSeekDirectPlayback(
  previous: DirectPlaybackSeekState | null,
  mediaKey: string,
  now: number,
  driftSeconds: number
): boolean {
  // A backward native seek can visibly replay an old scene and can trigger a
  // large byte-range request. Only catch up a lagging decoder.
  if (driftSeconds <= 0.75) return false;
  if (!previous || previous.mediaKey !== mediaKey) return true;
  return driftSeconds > directPlaybackCatchUpDriftSeconds
    && now - previous.at >= directPlaybackCatchUpIntervalMs;
}

export function isSeverelyShortMseChunk(appendedSeconds: number, expectedSeconds: number): boolean {
  return expectedSeconds >= 5 && appendedSeconds < expectedSeconds * 0.6;
}

export function isBackgroundMediaInterruption(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /background media was paused to save power|video-only background media was paused/i.test(message);
}

export function isBenignPlayInterruption(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /play\(\) request was interrupted by a call to pause\(\)/i.test(message);
}

export function shouldIgnoreDirectPlaybackEnded(
  releasing: boolean,
  currentTime: number,
  expectedDuration: number
): boolean {
  if (releasing) return true;
  return expectedDuration > playbackEndToleranceSeconds && currentTime < 0.25;
}

export function requiresNativePlayback(codec: string | null): boolean {
  const normalized = codec?.trim().toLowerCase() ?? "";
  return /^(?:hevc|h265|hvc1|hev1)(?:[.\s_-]|$)/.test(normalized);
}

export function playbackUrlWithRepresentation(url: string, representation: "mse" | "native"): string {
  if (/([?&])representation=(?:mse|native)(?=&|$)/.test(url)) {
    return url.replace(/([?&])representation=(?:mse|native)(?=&|$)/, `$1representation=${representation}`);
  }
  return `${url}${url.includes("?") ? "&" : "?"}representation=${representation}`;
}

function playbackUrlUsesRepresentation(url: string, representation: "mse" | "native"): boolean {
  return new RegExp(`(?:[?&])representation=${representation}(?:&|$)`).test(url);
}

function bufferedTimeIncludes(video: HTMLVideoElement, time: number): boolean {
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (time >= video.buffered.start(index) && time <= video.buffered.end(index)) return true;
  }
  return false;
}

export function bufferedPlaybackRecoveryTarget(video: HTMLVideoElement, desiredTime: number): number | null {
  const minimumTarget = Math.max(video.currentTime + 0.1, desiredTime);
  for (let index = 0; index < video.buffered.length; index += 1) {
    const start = video.buffered.start(index);
    const end = video.buffered.end(index);
    if (minimumTarget >= start && minimumTarget + localPlaybackRecoveryMinimumAdvanceSeconds < end) {
      return minimumTarget;
    }
    if (video.currentTime >= start && video.currentTime < end - 0.5) {
      return Math.min(video.currentTime + 0.1, end - localPlaybackRecoveryMinimumAdvanceSeconds);
    }
  }
  return null;
}

function directPlaybackRecoveryTarget(video: HTMLVideoElement, desiredTime: number): number | null {
  if (video.readyState < HTMLMediaElement.HAVE_METADATA || desiredTime <= video.currentTime + 0.75) return null;
  if (Number.isFinite(video.duration) && desiredTime >= video.duration) return null;
  return desiredTime;
}

function releaseVideoElement(video: HTMLVideoElement): void {
  video.dataset.nvrReleasing = "true";
  video.pause();
  video.removeAttribute("src");
  video.load();
}

function playbackMediaDiagnostic(
  video: HTMLVideoElement | null,
  event: PlaybackMediaDiagnostic["event"],
  desiredTime?: number
): PlaybackMediaDiagnostic {
  const finiteOrNull = (value: number) => Number.isFinite(value) ? value : null;
  let bufferedStart: number | null = null;
  let bufferedEnd: number | null = null;
  let droppedFrames: number | null = null;
  if (video) {
    try {
      if (video.buffered.length > 0) {
        bufferedStart = finiteOrNull(video.buffered.start(0));
        bufferedEnd = finiteOrNull(video.buffered.end(video.buffered.length - 1));
      }
      droppedFrames = video.getVideoPlaybackQuality().droppedVideoFrames;
    } catch {
      // Media ranges can become inaccessible while a resource is detached.
      // The remaining fields are still useful for diagnosing the failure.
    }
  }
  const currentTime = video ? finiteOrNull(video.currentTime) : null;
  return {
    event,
    errorCode: video?.error?.code ?? null,
    errorMessage: video?.error?.message?.slice(0, 500) || null,
    networkState: video?.networkState ?? HTMLMediaElement.NETWORK_EMPTY,
    readyState: video?.readyState ?? HTMLMediaElement.HAVE_NOTHING,
    currentTime,
    duration: video ? finiteOrNull(video.duration) : null,
    videoWidth: video?.videoWidth ?? 0,
    videoHeight: video?.videoHeight ?? 0,
    playbackRate: video ? finiteOrNull(video.playbackRate) : null,
    clockDriftSeconds: currentTime !== null && desiredTime !== undefined && Number.isFinite(desiredTime)
      ? desiredTime - currentTime
      : null,
    bufferedStart,
    bufferedEnd,
    droppedFrames
  };
}

function parseResolution(value: string | null | undefined): { width: number; height: number } | null {
  const match = value?.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (!match) return null;
  return { width: Number(match[1]), height: Number(match[2]) };
}

function seoulToday() { return seoulDateKey(new Date()); }
function formatSeoul(value: string) { return new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date(value)); }
function toSeoulInput(value: Date) {
  const parts = new Intl.DateTimeFormat("en", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}:${part("second")}`;
}
function fromSeoulInput(value: string) { return new Date(`${value}${value.length === 16 ? ":00" : ""}+09:00`).toISOString(); }
function formatBytes(value: number) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value, index = 0;
  while (size >= 1024 && index < units.length - 1) { size /= 1024; index++; }
  return `${size.toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}
function exportStatus(value: string) {
  return ({ queued: "대기 중", running: "내보내는 중", completed: "완료", failed: "실패", deleted: "삭제됨" } as Record<string, string>)[value] ?? value;
}
function selectionStyle(selection: { left: number; top: number; width: number; height: number }) {
  return { left: `${selection.left * 100}%`, top: `${selection.top * 100}%`, width: `${selection.width * 100}%`, height: `${selection.height * 100}%` };
}

export function isCurrentPlaybackRevision(currentRevision: number | undefined, eventRevision: number): boolean {
  return currentRevision === eventRevision;
}

export function samplePlaybackRecoveryAudit(
  previous: PlaybackRecoveryAuditWindow | undefined,
  now: number,
  intervalMs: number
): { report: boolean; suppressedCount: number; next: PlaybackRecoveryAuditWindow } {
  if (!previous || now - previous.lastReportedAt >= intervalMs) {
    return {
      report: true,
      suppressedCount: previous?.suppressedCount ?? 0,
      next: { lastReportedAt: now, suppressedCount: 0 }
    };
  }
  return {
    report: false,
    suppressedCount: 0,
    next: { ...previous, suppressedCount: previous.suppressedCount + 1 }
  };
}

export function timelineResponseCovers(timeline: Pick<TimelineResponse, "dayStart" | "dayEnd">, timeMs: number): boolean {
  return timeMs >= new Date(timeline.dayStart).getTime() && timeMs < new Date(timeline.dayEnd).getTime();
}

export function recordingRangeAt(ranges: TimelineRange[], target: Date): TimelineRange | null {
  const targetMs = target.getTime();
  return ranges.find((range) => targetMs >= new Date(range.startAt).getTime() && targetMs < new Date(range.endAt).getTime()) ?? null;
}

export function resolveRecordingTarget(ranges: TimelineRange[], target: Date, direction: SeekDirection): { range: TimelineRange; at: Date; adjusted: boolean } | null {
  const targetMs = target.getTime();
  const containing = recordingRangeAt(ranges, target);
  if (containing) {
    const start = new Date(containing.startAt).getTime();
    const end = new Date(containing.endAt).getTime();
    const safeEnd = Math.max(start + 1, end - 250);
    if (direction === "forward" && targetMs > safeEnd) {
      const containingIndex = ranges.indexOf(containing);
      const next = ranges[containingIndex + 1];
      if (!next) return null;
      return { range: next, at: new Date(new Date(next.startAt).getTime() + 1), adjusted: true };
    }
    const safeAt = Math.min(safeEnd, Math.max(start + 1, targetMs));
    return { range: containing, at: new Date(safeAt), adjusted: safeAt !== targetMs };
  }
  if (direction === "forward") {
    const next = ranges.find((range) => new Date(range.startAt).getTime() > targetMs);
    if (!next) return null;
    return { range: next, at: new Date(new Date(next.startAt).getTime() + 1), adjusted: true };
  }
  const previous = [...ranges].reverse().find((range) => new Date(range.endAt).getTime() <= targetMs);
  if (!previous) return null;
  const previousStart = new Date(previous.startAt).getTime();
  return { range: previous, at: new Date(Math.max(previousStart + 1, new Date(previous.endAt).getTime() - 250)), adjusted: true };
}

function defaultTimelineWindow(now: Date): TimelineWindow {
  return timelineWindowAt(now, timelineZoomDurations[defaultTimelineZoomLevel]!, timelineFocusRatio);
}

export function timelineLoadPadding(view: TimelineWindow): number {
  return Math.max(minimumTimelineLoadPaddingMs, view.end - view.start);
}

export function shouldRefreshTimelineWindow(view: TimelineWindow, now = Date.now()): boolean {
  return view.end >= now - liveTimelineRefreshGraceMs;
}

function timelineWindowAt(focus: Date, duration: number, position: number): TimelineWindow {
  const time = focus.getTime();
  return { start: time - duration * position, end: time + duration * (1 - position) };
}

function timelineReferenceTime(view: TimelineWindow): number {
  return view.start + (view.end - view.start) * timelineFocusRatio;
}

function rangePlacement(range: { startAt: string; endAt: string }, view: { start: number; end: number }) {
  const start = Math.max(view.start, new Date(range.startAt).getTime());
  const end = Math.min(view.end, new Date(range.endAt).getTime());
  if (end <= start) return null;
  const duration = view.end - view.start;
  const left = ((start - view.start) / duration) * 100;
  const right = ((end - view.start) / duration) * 100;
  return { left: `${left}%`, width: `${Math.max(0.1, right - left)}%` };
}

function formatTimelineTick(value: Date, previous: Date | undefined, showDate: boolean): string {
  const local = toSeoulInput(value);
  const date = local.slice(0, 10);
  const time = local.slice(11, 16);
  if (!showDate) return time;
  const previousDate = previous ? toSeoulInput(previous).slice(0, 10) : "";
  return !previous || date !== previousDate ? `${local.slice(5, 7)}.${local.slice(8, 10)}. ${time}` : time;
}

function seoulDateKey(value: Date): string { return toSeoulInput(value).slice(0, 10); }

function formatTimelineHover(value: Date): string {
  const local = toSeoulInput(value);
  const time = local.slice(11, 19);
  return local.slice(0, 10) === seoulToday() ? time : `${local.slice(5, 7)}.${local.slice(8, 10)}. ${time}`;
}
